import * as yup from "yup";

import {
	ACCOUNTANT_SETUP_SCREEN_SLUG,
	INITIAL_SNACKBAR_DATA,
} from "../../../common/constants";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedSnackbar,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../../models/common";
import React, { useEffect, useState } from "react";
import { formatDate, scrollPanelFieldsToTop, useNavigateToLogout } from "../../../common/Common";
import { setSelectedAccountant, signInUpdate } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { AccountantAddressSetupFieldsPanel } from "./Panels/AccountantAddressSetupFieldsPanel";
import { AccountantInfoSetupFieldsPanel } from "./Panels/AccountantInfoSetupFieldsPanel";
import { AccountantService } from "../../../services";
import { AccountantSetupToolbar } from "./AccountantSetupToolbar/AccountantSetupToolbar";
import { AccountantSetupValidationSchema } from "./validator";
import { BasicSetupFormToolbar } from "../../BasicSetupFormToolbar";
import { CityService } from "../../../services/CityService";
import { CountryService } from "../../../services/common/CountryService";
import { Grid } from "@mui/material";
import { IAccountant } from "../../../models/accountant";
import { IAppState } from "../../../store/reducers";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IAccountantSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY: any = "accountant-single-data-record";

const useGetData = (id: string, navigateToLogout: (path: string) => void) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => AccountantService.getById(id, navigateToLogout),
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: () => CountryService.getCountryDDL(),
		refetchOnWindowFocus: false,
	});

const useGetProvinceOrStateDDL = (
	navigateToLogout: (path: string) => void,
	countryId: string,
	countryTag: string
) =>
	useQuery({
		queryKey: ["province-ddl"],
		queryFn: () =>
			CountryService.getProvinceOrStateDDL(
				navigateToLogout,
				countryId,
				countryTag
			),
		refetchOnWindowFocus: false,
	});

export const AccountantSetup: React.FC<IAccountantSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [isPendingData, setIsPendingData] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [accountantId, setAccountantId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [provinceDDLLoading, setProvinceDDLLoading] = useState(false);
	const [provinceStateDDLinUpdate, setProvinceStateDDLinUpdate] =
		useState(undefined);
	const [cityDDLinUpdate, setCityDDLinUpdate] = useState(undefined);
	const [provinceStateLabelinUpdate, setProvinceStateLabelinUpdate] =
		useState<any>(undefined);
	const queryClient = useQueryClient();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const companyAccountantName = useSelector(
		(state: IAppState) => state.user.profile.companyAccountantName
	);
	const navigateToLogout = useNavigateToLogout();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState<any>(id);

	useEffect(() => {
		if (companyAccountantName !== null && isAdminUser === 100) {
			formMethods.setValue('accTitle', companyAccountantName)
		}
	}, [companyAccountantName])

	const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
		mutationFn: AccountantService.create,
		onSuccess: (res: IAccountant | IServerError | IResponse | any) => {
			if (res.status === 200) {
				// if (instanceOfIAccountantResponse(res)) {
				setEditRecordId(res.id);
				queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				scrollPanelFieldsToTop();
				let path = `/accountants/list`;
				navigate(path);
			} else if (res.status === 201) {
				// if (instanceOfIAccountantResponse(res)) {
				setEditRecordId(res.id);
				queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				scrollPanelFieldsToTop();
				let path = `/accountants/list`;
				navigate(path);
				if (companyAccountantName !== null) {
					dispatch(
						signInUpdate({
							user: { companyAccountantName: null },
						})
					);
					dispatch(setSelectedAccountant({ id: res.data.id, AccountantName: res.data.accTitle }));
				}
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const { isPending: isPendingCountryDDL, data: countryList } =
		useGetCountryDDL();
	// let countryList = [
	// 	{
	// 		"shortCode": "CAN",
	// 		"value": "6718c0e02bd6872c87d35d0f",
	// 		"label": "Canada",
	// 		"countryType": "Province"
	// 	},
	// 	{
	// 		"logo": "",
	// 		"shortCode": "USA",
	// 		"value": "67496d974b9fb9f2ce4213b9",
	// 		"label": "United States of America",
	// 		"countryType": "state"
	// 	}
	// ]


	const ExtendedValidationSchema = AccountantSetupValidationSchema.shape({
		postCode: yup.string().when("country", (country: any, schema: any) => {
			const selectedCountry = countryList.find(
				(c: any) => c.value === country[0]
			);
			if (selectedCountry?.shortCode === "CAN") {
				return schema
					.required("Postal Code is required")
					.length(6, "Postal Code must be exactly 6 characters");
			}
			return schema; // Unchanged for other countries
		}),
		zipCode: yup.string().when("country", (country: any, schema: any) => {
			const selectedCountry = countryList.find(
				(c: any) => c.value === country[0]
			);
			if (selectedCountry?.shortCode === "USA") {
				return schema
					.required("Zip Code is required")
					.length(5, "Zip Code must be exactly 5 characters");
			}
			return schema; // Unchanged for other countries
		}),
		startDate: yup
			.string()
			.nullable()
			.test("startDate-required", "Start Date is required", function (value) {
				if (isAdminUser === 400) {
					return !!value;
				}
				return true;
			}),
	});

	const DEFAULT_VALUES = {
		accTitle: undefined,
		firstName: undefined,
		lastName: undefined,
		initial: undefined,
		phone: undefined,
		fax: undefined,
		email: undefined,
		startDate: null,
		endDate: null,
		addressLine1: undefined,
		// country: undefined,
		province: undefined,
		postCode: undefined,
		supportAccessLevelCanManage: 100,
		type: 100,
		activateAccount: false,
		defaultAccountant: false,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(ExtendedValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = async (values: any) => {
		// setSnackbarData({
		// 	open: true,
		// 	message: "Success",
		// 	severity: "success",
		// });
		const SelectedCountry = countryList.find(
			(e: any) => e.value === values.country
		);
		try {
			setIsPendingData(true);
			const ProvinceOrStateDDL = await CountryService.getProvinceOrStateDDL(
				navigateToLogout,
				SelectedCountry.value,
				SelectedCountry.countryType
			);
			const selectedProvinceOrState = values.province
				? ProvinceOrStateDDL.find((e: any) => e.value === values.province)
				: values.state
					? ProvinceOrStateDDL.find((e: any) => e.value === values.state)
					: undefined;

			let payload = {
				id: id && id,
				superUser: isAdminUser,
				accTitle: values.accTitle,
				firstName: values.firstName,
				lastName: values.lastName,
				initial: values.initial,
				supportAccessLevelCanManage: values.supportAccessLevelCanManage,
				startDate:
					values.startDate &&
					values.startDate !== "" &&
					formatDate(values.startDate),
				endDate:
					values.endDate && values.endDate !== "" && formatDate(values.endDate),
				type: values.type,
				status: 100,
				activateAccount: values.activateAccount,
				defaultAccountant: values.defaultAccountant,
				address: {
					countryType: values.province ? true : false,
					country: {
						id: SelectedCountry.value,
						name: SelectedCountry.label,
						shortCode: SelectedCountry.shortCode,
					},
					province: values.province
						? {
							id: selectedProvinceOrState.value,
							name: selectedProvinceOrState.label,
							shortCode: selectedProvinceOrState.shortCode,
						}
						: undefined,
					states: values.state
						? {
							id: selectedProvinceOrState.value,
							name: selectedProvinceOrState.label,
							shortCode: selectedProvinceOrState.shortCode,
						}
						: undefined,
					addressLine1: values.addressLine1,
					city: values.city,
					// postCode: values.postCode ? values.postCode : undefined,
					postCode: values.postCode
						? values.postCode.replace(/[a-z]/g, (match: string) =>
							match.toUpperCase()
						)
						: undefined,
					zipCode: values.zipCode ? values.zipCode : undefined,
					email: values.email,
					phone: values.phone,
					fax: values.fax,
				},
				navigateToLogout: navigateToLogout,
			};

			//   alert(JSON.stringify(payload, null, 2));
			submitFormFields(payload);
		} catch {
		} finally {
			setIsPendingData(false);
		}
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				// @ts-ignore
				const fieldValue = selectedDataRecord[fieldName];
				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				// 		if (fieldName === 'customerName'){
				//   formMethods.setValue('name',fieldValue)
				// 		}
				if (fieldName === "address") {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						if (objFieldName === "countryType") {
							(async () => {
								try {
									setProvinceDDLLoading(true);
									const responce = await CountryService.getProvinceOrStateDDL(
										navigateToLogout,
										selectedDataRecord.address.country.id,
										objFieldValue ? "province" : "state"
									);

									setProvinceStateDDLinUpdate(responce);
									setProvinceStateLabelinUpdate(
										objFieldValue ? "province" : "state"
									);
								} catch { } finally {
									setProvinceDDLLoading(false);
								}
							})();
						}
						if (objFieldName === "country") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
						if (objFieldName === "province") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
							(async () => {
								try {
									setProvinceDDLLoading(true);
									const responce = await CityService.getCityDDL(
										navigateToLogout,
										objFieldValue.id
									);
									setCityDDLinUpdate(responce);
								} catch {
									setProvinceDDLLoading(false);
								} finally {
									setProvinceDDLLoading(false);
								}
							})();
						}
						if (objFieldName === "state") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
						if (objFieldName === "city") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
					});
				}
				// }
				if (fieldName === "startDate") {
					if (fieldValue !== null) {
						const defaultDate = new Date(fieldValue);
						formMethods.setValue(fieldName, defaultDate);
					} else {
						formMethods.setValue(fieldName, fieldValue);
					}
				}
				if (fieldName === "endDate") {
					if (fieldValue !== null) {
						const defaultDate = new Date(fieldValue);
						formMethods.setValue(fieldName, defaultDate);
					} else {
						formMethods.setValue(fieldName, fieldValue);
					}
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteAccountantById } = useMutation({
		mutationFn: AccountantService.deleteById,
		onSuccess: (res: string | IServerError | IResponse | any) => {
			if (
				typeof res === "object" &&
				res?.message === "Accountant has been deleted successfully"
			) {
				// remove the cache of the Accountant fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/accountants/setup`;
				navigate(path);
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteAccountantField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				accountantId: accountantId,
				navigateToLogout: navigateToLogout,
			};
			deleteAccountantById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setAccountantId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/accountants/setup`;
		navigate(path);
	};

	const expandedLabels = ["Accountant"];
	const expandedAccountantSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Accountant" &&
				item.label === expandedAccountantSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedAccountantSetupOrSetupItem && "/accountants/setup",
			};
			return enhancedItem;
		});
	};

	// const enhanceData = (data: any[]): any[] => {
	// 	return data.map((item) => {
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: item.label === "Setup" ? true : false,
	// 		};
	// 		return enhancedItem;
	// 	});
	// };

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
				accountantName: "",
				activityType:
					"activity_type=create&activity_type=updated&activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingData ||
					isPendingById ||
					isDeleting ||
					provinceDDLLoading
				}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteAccountantField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid
							size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
						// sx={{
						// 	border: "1px solid rgba(0, 0, 0, 0.12)",
						// 	borderRadius: "3px",
						// 	marginTop: "16px",
						// 	background: "#ffffff",
						// }}
						>
							<TwoColumnLayout
								leftColumnContent={
									<StandardPanel
										title="Create Accountant"
										testId={`accountant`}
										// leftSpacing={1}
										// rightSpacing={1}
										variant="outlined"
										borderHide={true}
									>
										<AccountantInfoSetupFieldsPanel
											// countryList={countryList}
											// formMethods={formMethods}
											// provinceDDLLoading={(value: boolean) =>
											// 	setProvinceDDLLoading(value)
											// }
											// provinceStateLabelinUpdate={
											// 	provinceStateLabelinUpdate && provinceStateLabelinUpdate
											// }
											// provinceStateDDLinUpdate={
											// 	provinceStateDDLinUpdate && provinceStateDDLinUpdate
											// }
											isAdminUser={isAdminUser}
										/>
									</StandardPanel>
								}
								rightColumnContent={
									<StandardPanel
										title="Address Info"
										testId={`accountant`}
										// leftSpacing={1}
										// rightSpacing={1}
										variant="outlined"
										borderHide={true}
									>
										<AccountantAddressSetupFieldsPanel
											countryList={countryList}
											formMethods={formMethods}
											provinceDDLLoading={(value: boolean) =>
												setProvinceDDLLoading(value)
											}
											provinceStateLabelinUpdate={
												provinceStateLabelinUpdate && provinceStateLabelinUpdate
											}
											provinceStateDDLinUpdate={
												provinceStateDDLinUpdate && provinceStateDDLinUpdate
											}
											cityDDLinUpdate={cityDDLinUpdate && cityDDLinUpdate}
											isAdminUser={isAdminUser}
										/>
									</StandardPanel>
								}
								toolbar={
									// <AccountantSetupToolbar
									// 	onResetClicked={onResetClicked}
									// 	onDeleteClicked={onDeleteClicked}
									// 	isSubmitting={false}
									// 	editRecordId={editRecordId}
									// />
									<BasicSetupFormToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										// editRecordId={editRecordId}
										pageTitle="Accountant Setup"
										id={id}
									/>
								}
								testId="form-layout"
								toolbarPosition="top"
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"accountant-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
